/**
 * Structural pass: runs immediately after `rehype-raw`, before the table-title
 * lift. Policy-independent — it registers under every typesetting policy,
 * because a namespace scrubbed only on the editorial path would leave the
 * plain and fail-open paths spoofable.
 *
 * Two jobs, no text mutation: strip the reserved namespaces from anything the
 * source authored, and record authored heading/hr adjacency while it still
 * exists.
 */
import type { EditorialHastNode } from "./editorial-capabilities";
import { isIgnorableNode } from "./editorial-inline-run";
import {
    getEditorialProvenance,
    isEditorialGenerated,
    markEditorial,
    normalizeAttributeName,
} from "./editorial-provenance";

type HastNodeLike = EditorialHastNode;

const STRUCTURAL_TAGS: ReadonlySet<string> = new Set([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "hr",
]);

/**
 * The single definition of "reserved", and the seam to extend.
 *
 * A property belongs here when the RENDERER treats it as authority rather than
 * as content — when its presence alone changes how the answer is presented.
 * That is the test to apply before adding one, not "is it ours".
 *
 * Exported because the invariant harness asks the same question of rendered
 * output, and its own copy of the list had already fallen a name behind — so a
 * forged `data-inferred-title` was scrubbed in production and unchecked by the
 * invariant that exists to prove it.
 */
export const isReservedProperty = (name: string): boolean => {
    const normalized = normalizeAttributeName(name);
    return (
        normalized.startsWith("dataeditorial") ||
        normalized === "dataauistreamtail" ||
        // The table toolbar's caption. It qualifies on the test above: the
        // toolbar reads it off the node and renders it as chrome, so an
        // authored value is displayed as system-generated metadata rather than
        // as message content — a table captioned with words the model wrote.
        //
        // Safe to scrub ONLY because this pass runs before `rehypeInferTableTitle`
        // under both policies. Reversed, it would delete the title the lift had
        // just inferred, which is a worse defect than the one it fixes. That
        // order is asserted in `editorial-pipeline.test.ts`, not assumed here.
        normalized === "datainferredtitle"
    );
};

// Deletes in place rather than rebuilding the object: the markdown memo
// comparator tests `properties` by reference, so a fresh object on an
// otherwise unchanged node would bust memoization during streaming.
//
// Written so it cannot throw, which matters more here than it looks: the scrub
// also runs on the PLAIN path, and the plain path is what the fail-open
// boundary renders. There is no second boundary underneath it, so a scrubber
// that threw on a malformed node would turn the safety net into the crash.
// Hence the shape checks rather than trust: `properties` typed as an object is
// not the same as `properties` being one.
const scrubReservedProperties = (node: HastNodeLike): void => {
    const properties = node.properties;
    if (typeof properties !== "object" || properties === null) return;

    for (const name of Object.keys(properties)) {
        if (isReservedProperty(name)) denyProperty(properties, name);
    }
};

/**
 * Remove a property, or failing that make it meaningless, or failing that give
 * up quietly.
 *
 * A sealed `properties` refuses the delete but accepts the assignment; a frozen
 * one refuses both. Blanking is a real second line rather than a gesture —
 * every treatment selector matches a specific value, and hast serializes
 * `undefined` to no attribute at all.
 *
 * Giving up is the last resort and it is deliberate. A forged attribute on a
 * frozen node is a spoofing risk; an exception here is a blank thread, because
 * the plain path is the fallback and has nothing to fall back to.
 */
const denyProperty = (
    properties: Record<string, unknown>,
    name: string
): void => {
    try {
        delete properties[name];
        return;
    } catch {
        // Fall through to blanking.
    }
    try {
        properties[name] = undefined;
    } catch {
        // Frozen both ways; nothing further is possible from here.
    }
};

const meaningfulSibling = (
    siblings: readonly HastNodeLike[],
    index: number,
    step: 1 | -1
): HastNodeLike | undefined => {
    for (let i = index + step; i >= 0 && i < siblings.length; i += step) {
        const candidate = siblings[i];
        if (!isIgnorableNode(candidate)) return candidate;
    }
    return undefined;
};

const structuralNeighbourTag = (
    siblings: readonly HastNodeLike[],
    index: number,
    step: 1 | -1
): string | undefined => {
    const neighbour = meaningfulSibling(siblings, index, step);
    if (neighbour?.type !== "element") return undefined;

    const tagName = neighbour.tagName ?? "";
    return STRUCTURAL_TAGS.has(tagName) ? tagName : undefined;
};

/**
 * Recorded here because `rehypeInferTableTitle` may REMOVE the heading when it
 * lifts a table title. Provenance is a symbol on the node, so the mark survives
 * that splice; reading adjacency downstream would not.
 *
 * Recorded in BOTH directions, because a one-way record only survives when the
 * lift happens to consume the neighbour on the recorded side. For `hr` then
 * heading, the sole mark sat on the heading — the node the lift removes — and
 * the surviving rule was left with no evidence that it had ever been
 * heading-adjacent, which is exactly what rule suppression has to decide on.
 */
const recordAuthoredAdjacency = (
    siblings: readonly HastNodeLike[],
    index: number,
    node: HastNodeLike
): void => {
    const tagName = node.tagName ?? "";
    if (!STRUCTURAL_TAGS.has(tagName)) return;

    const authoredPrevious = structuralNeighbourTag(siblings, index, -1);
    const authoredNext = structuralNeighbourTag(siblings, index, 1);
    if (authoredPrevious === undefined && authoredNext === undefined) return;

    // Absent keys rather than explicit `undefined`: one `markEditorial` call
    // replaces the whole frozen record, so the shape it writes is the shape
    // consumers read.
    markEditorial(node, {
        kind: "structural-mark",
        ...(authoredPrevious !== undefined && { authoredPrevious }),
        ...(authoredNext !== undefined && { authoredNext }),
    });
};

/**
 * Scrub only, under EVERY policy.
 *
 * Split from the pass below rather than registering that one on both paths,
 * because the two jobs have different owners. Scrubbing is a trust boundary and
 * belongs wherever a message can be rendered. Recording heading adjacency is
 * editorial input — it exists to feed rule normalization, which only the
 * editorial path runs — and doing it on the plain path would be dead work that
 * marks the plain tree with provenance nothing there reads.
 *
 * Walks everything, with no minted-subtree skip: on the plain path nothing is
 * minted, so every node in the tree came from the message and every node is
 * suspect.
 */
export const rehypeScrubReservedNamespace =
    () =>
    (tree: unknown): void => {
        if (!tree || typeof tree !== "object") return;

        // An explicit stack rather than recursion, and this is the one place
        // the difference is load-bearing. A message can author arbitrarily deep
        // markup, and the recursive form threw `RangeError: Maximum call stack
        // size exceeded` from depth 5000 — measured, not feared. On this path
        // that error has nothing above it to catch it, so a nested-enough
        // message would blank the thread instead of merely rendering plainly.
        //
        // `seen` is what keeps the iterative form honest. Recursion turns a
        // cyclic tree into a RangeError; a bare stack turns it into an infinite
        // loop, and a hung render is worse than a thrown one. Parsers do not
        // emit cycles, so this is defence against a future plugin rather than
        // against the source — one Set per pass, and the question is closed.
        const pending: HastNodeLike[] = [tree as HastNodeLike];
        const seen = new Set<unknown>();

        while (pending.length > 0) {
            const node = pending.pop() as HastNodeLike;
            if (seen.has(node)) continue;
            seen.add(node);

            scrubReservedProperties(node);

            const children = node.children;
            if (!Array.isArray(children)) continue;
            for (const child of children) {
                if (child && typeof child === "object") pending.push(child);
            }
        }
    };

// Explicit stack, matching the scrub above. This walk's recursion depth IS the
// tree depth, so ~10k nested elements threw `RangeError: Maximum call stack size
// exceeded` and defeated fail-open: the boundary caught it, fell back to plain,
// and the anchor pass then threw on the same tree.
//
// Adjacency is still computed per PARENT, from that parent's `children` array
// and the child's index within it, so nothing about sibling semantics changes.
// Only the descent order does — depth-first from the last child rather than the
// first — and neither scrubbing nor adjacency depends on visit order.
//
// `seen` guards cycles, for the same reason as everywhere else in this file: a
// bare stack turns a cyclic tree into a hang instead of a throw.
const visitChildren = (parent: HastNodeLike): void => {
    const seen = new Set<unknown>();
    const stack: HastNodeLike[] = [parent];

    while (stack.length > 0) {
        const current = stack.pop() as HastNodeLike;
        if (seen.has(current)) continue;
        seen.add(current);

        const children = current.children;
        if (!children) continue;

        for (let index = 0; index < children.length; index++) {
            const child = children[index];
            if (child?.type !== "element") continue;

            // Minted output keeps its reserved properties, and its generated
            // subtree is never re-walked — re-scrubbing our own markup would
            // erase the marks the terminal pass just wrote. Gated on MINTED
            // provenance, not on any provenance: an adjacency mark annotates a
            // SOURCE node, and treating it as a skip point would leave that
            // node's subtree unscrubbed on a repeat pass.
            if (isEditorialGenerated(child)) continue;

            scrubReservedProperties(child);
            recordAuthoredAdjacency(children, index, child);
            stack.push(child);
        }
    }
};

export const rehypeMarkEditorialStructure =
    () =>
    (tree: unknown): void => {
        // This pass annotates the AUTHORED tree, exactly once. Running it again
        // over its own output re-derives adjacency from a tree the later passes
        // have already edited, which is not the same question.
        //
        // Measured, on the generated case `--- / decorative-only paragraph /
        // --- / ## Heading`: the paragraph is suppressed and the second rule is
        // removed, so on a repeat pass the SURVIVING rule — which had no
        // structural neighbour in the source and therefore carries no mark —
        // acquires `authoredNext: "h2"` and rule normalization deletes a rule
        // the reader wrote. Guarding per node cannot fix that: "no adjacency
        // recorded" and "never visited" are the same state on a node, and only
        // the pass knows the difference.
        //
        // Scrubbing is skipped with it, correctly: the input to a repeat pass is
        // this layer's own output, which was scrubbed the first time through.
        const root = tree as HastNodeLike;
        if (getEditorialProvenance(root) !== undefined) return;
        markEditorial(root, { kind: "structural-mark" });

        visitChildren(root);
    };
